/**
 * PricingLegend Component
 *
 * Displays a gradient legend for pricing choropleth layers
 */

import { useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { getPricingLegendConfig } from '../../config/pricingLegendConfig'

const PRICING_LEGEND_TRANSLATION_KEYS: Record<string, string> = {
  'purchase-market-apartment-price': 'purchaseMarketApartmentPrice',
  'purchase-market-house-price': 'purchaseMarketHousePrice',
  'rental-market-apartment-price': 'rentalMarketApartmentPrice',
  'rental-market-house-price': 'rentalMarketHousePrice',
}

export interface PricingLegendProps {
  /** Layer ID to display legend for */
  layerId: string
  /** Optional CSS class name */
  className?: string
}

/**
 * PricingLegend Component
 *
 * Renders a gradient legend for pricing layers with color bands and value ranges
 */
export const PricingLegend = ({ layerId, className }: PricingLegendProps) => {
  const { t } = useTranslation('ui')
  const legendConfig = useMemo(() => getPricingLegendConfig(layerId), [layerId])
  const translationKey = PRICING_LEGEND_TRANSLATION_KEYS[layerId]

  if (!legendConfig) {
    return null
  }

  // Create colors and positions arrays for continuous gradient
  const colors = legendConfig.entries.map(
    (entry: { color: string; label: string }) => entry.color
  )
  const positions = legendConfig.entries.map(
    (_: { color: string; label: string }, index: number) =>
      (index / (legendConfig.entries.length - 1)) * 100
  )

  return (
    <LegendContainer className={className}>
      <LegendTitle>
        {translationKey
          ? t(`analysisMap.layers.${translationKey}`)
          : legendConfig.title}
      </LegendTitle>

      <GradientBar $colors={colors} $positions={positions} />

      <LabelsContainer>
        {legendConfig.entries.map(
          (entry: { color: string; label: string }, index: number) => (
            <Label
              key={index}
              $position={(index / (legendConfig.entries.length - 1)) * 100}
            >
              {entry.label}
            </Label>
          )
        )}
      </LabelsContainer>

      {legendConfig.unit && <Unit>{legendConfig.unit}</Unit>}
    </LegendContainer>
  )
}

// Styled Components

const LegendContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`

const LegendTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const GradientBar = styled.div<{ $colors: string[]; $positions: number[] }>`
  height: 12px;
  border-radius: 2px;
  background: ${({ $colors, $positions }) => {
    if ($colors.length === 0) return '#CCCCCC'
    if ($colors.length === 1) return $colors[0]

    const stops = $colors
      .map((color, index) => `${color} ${$positions[index]}%`)
      .join(', ')
    return `linear-gradient(to right, ${stops})`
  }};
  margin-bottom: 4px;
`

const LabelsContainer = styled.div`
  position: relative;
  height: 16px;
  margin-top: 2px;
`

const Label = styled.div<{ $position: number }>`
  position: absolute;
  left: ${({ $position }) => $position}%;
  transform: translateX(-50%);
  font-size: 9px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;

  &:first-child {
    transform: none;
    left: 0;
  }

  &:last-child {
    transform: none;
    left: auto;
    right: 0;
  }
`

const Unit = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
  font-style: italic;
`
